import { useEffect, useState } from 'react';
import {
  CalculateRentalPriceResult,
  CalculateSalePriceResult,
  ProductPrice,
} from '../../clients/quipli-service/pricing/pricing';
import { DateTime, Duration, Interval } from 'luxon';
import { DateTimePicker } from '../../components/date-picker/date-time-picker';
import { ProductPricingTable } from '../../customer/product-pricing-table/product-pricing-table';
import Select from 'react-select';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { OrderType } from '../../clients/quipli-service/order/order';
import { safeRound } from '../../util/util';

export const PricingPreview = ({
  productId,
  forRent,
  forSale,
  locations,
  prices,
  showRangePriceUnits,
}: {
  productId: number;
  forRent: boolean;
  forSale: boolean;
  locations: { value: number; label: string }[];
  prices: ProductPrice[];
  showRangePriceUnits: boolean;
}) => {
  const [selectedPriceType, setSelectedPriceType] = useState<OrderType>(forRent ? OrderType.Rental : OrderType.Sale);
  const [previewRentalStart, setPreviewRentalStart] = useState<DateTime | undefined>(
    DateTime.now().plus({ hours: 25 }).set({ minute: 0 }),
  );
  const [previewRentalEnd, setPreviewRentalEnd] = useState<DateTime | undefined>(previewRentalStart?.plus({ day: 1 }));
  const [location, setLocation] = useState<{ value: number; label: string }>(locations[0]);
  const [units, setUnits] = useState<number>(1);
  const [calculatedPriceResult, setCalculatedPriceResult] = useState<CalculateRentalPriceResult>();
  const [calculatedSalePrice, setCalculatedSalePrice] = useState<number>();
  const [formattedDuration, setFormattedDuration] = useState<string>('');

  const { calculatePrices } = useCustomerAPI();
  const currencyFormat = Intl.NumberFormat('us-en', { currency: 'USD', minimumFractionDigits: 2 });

  useEffect(() => {
    if (previewRentalStart && previewRentalEnd && previewRentalStart < previewRentalEnd)
      calculatePrices([
        {
          productId,
          type: OrderType.Rental,
          start: previewRentalStart,
          end: previewRentalEnd,
          locationId: location?.value,
        },
      ]).then((res) => {
        if (res.length > 0) {
          const result = res[0] as CalculateRentalPriceResult;
          setCalculatedPriceResult(result);
          const dur = Duration.fromObject({ minutes: result.totalDurationMinutes });
          const asDays = dur.as('days');
          const asHours = safeRound(dur.as('hours')) % 24;
          const daysStr = `${safeRound(asDays)} ${safeRound(asDays) >= 2 ? 'days' : 'day'}`;
          const hourStr = `${asHours} ${asHours > 1 ? 'hours' : 'hour'}`;
          const durationString = `${asDays >= 1 ? daysStr : ''}${asDays >= 1 && asHours > 0 ? ', ' : ''}${
            asHours > 0 ? hourStr : ''
          }`;
          setFormattedDuration(durationString);
        }
      });
  }, [previewRentalStart, previewRentalEnd, location, prices]);

  useEffect(() => {
    if (forSale) {
      calculatePrices([{ productId, type: OrderType.Sale, locationId: location?.value }]).then((res) => {
        setCalculatedSalePrice((res[0] as CalculateSalePriceResult).totalPrice);
      });
    }
  }, [location, productId, prices]);

  // TODO: separate out the pricing calendar stuff into separate component for customer facing...
  return (
    <div className="pricing-preview font-sans font-light text-sm text-gray-700">
      <div className={`grid ${!forRent ? 'grid-cols-2 justify-between' : 'grid-cols-3 gap-4'}`}>
        <div className={!forRent ? 'col-span-1' : 'col-span-2'}>
          <div className="flex gap-5 items-center mb-4">
            <div className="text-md font-normal">Location</div>
            <Select
              className="min-w-[40%]"
              name="Location"
              options={locations}
              value={location}
              onChange={(loc) => {
                if (loc) setLocation(loc);
              }}
            />
          </div>

          {forRent && (
            <ProductPricingTable
              productId={productId}
              locationId={location.value}
              showRangePriceUnitsConf={showRangePriceUnits}
              tagDiscount={1}
            />
          )}
        </div>

        <div className="col-span-1">
          <div className="bg-zinc-100 p-5">
            <div className="text-lg text-gray-700">
              {forRent && forSale ? 'Rent or buy now' : forSale ? 'Buy now' : 'Rent now'}
            </div>
            {forSale && forRent && (
              <div className="flex gap-8">
                <a
                  onClick={() => setSelectedPriceType(OrderType.Rental)}
                  className="border border-solid border-gray-300 flex items-center p-2 rounded-md"
                >
                  {selectedPriceType === OrderType.Rental ? (
                    <div
                      className={`ml-2 h-6 w-6 border-[6px] border-solid border-sky-600 rounded-full bg-white mr-8`}
                    ></div>
                  ) : (
                    <div className={`ml-2 h-6 w-6 border border-solid border-sky-600 rounded-full bg-white mr-8`}></div>
                  )}

                  <div>I want to rent</div>
                </a>
                <a
                  onClick={() => setSelectedPriceType(OrderType.Sale)}
                  className="border border-solid border-gray-300 flex items-center p-2 rounded-md"
                >
                  {selectedPriceType === OrderType.Sale ? (
                    <div
                      className={`ml-2 h-6 w-6 border-[6px] border-solid border-sky-600 rounded-full bg-white mr-8`}
                    ></div>
                  ) : (
                    <div className={`ml-2 h-6 w-6 border border-solid border-sky-600 rounded-full bg-white mr-8`}></div>
                  )}

                  <div>I want to buy</div>
                </a>
              </div>
            )}

            {selectedPriceType === OrderType.Rental && (
              <>
                <div className="rent-from">
                  <span className="text-sm text-gray-700">Rent from</span>
                  <DateTimePicker
                    name="Rent from"
                    value={previewRentalStart}
                    onChange={(d) => setPreviewRentalStart(d)}
                    disabledIntervals={[Interval.before(DateTime.now(), { years: 10 })]}
                  />
                </div>
                <div className="rent-to">
                  <span className="text-sm text-gray-700">Rent to</span>
                  <DateTimePicker
                    name="Rent to"
                    value={previewRentalEnd}
                    onChange={(d) => setPreviewRentalEnd(d)}
                    disabledIntervals={[Interval.before(previewRentalStart || DateTime.now(), { years: 10 })]}
                  />
                </div>
              </>
            )}

            <div className="mt-3 grid grid-cols-5 gap-4 items-end">
              <div className="col-span-2 flex items-center border border-solid rounded border-gray-600 p-3 w-full bg-white justify-between">
                <i
                  className={`fa-solid fa-minus disabled ${units === 1 ? 'hover:disabled' : 'hover:cursor-pointer'}`}
                  onClick={() => {
                    if (units > 1) setUnits(units - 1);
                  }}
                />
                <span className="text-sm">{units}</span>
                <i className="fa-solid fa-plus hover:cursor-pointer" onClick={() => setUnits(units + 1)} />
              </div>
              <div className="col-span-3">
                <div className="mt-2 duration">
                  {selectedPriceType === OrderType.Rental
                    ? `Price for ${calculatedPriceResult ? formattedDuration : 'N/A'}`
                    : 'Price'}
                </div>
                <div className="font-bold">
                  {selectedPriceType === OrderType.Rental && calculatedPriceResult
                    ? `$${currencyFormat.format((calculatedPriceResult.totalPrice / 100) * units)}`
                    : selectedPriceType === OrderType.Sale && calculatedSalePrice !== undefined
                    ? `$${currencyFormat.format((calculatedSalePrice / 100) * units)}`
                    : 'N/A'}
                </div>
              </div>
            </div>
          </div>

          <div className="border border-solid rounded border-gray-300 my-3 bg-white p-3">
            <div className="text-sm mb-2 underline">Pricing Breakdown</div>
            {selectedPriceType === OrderType.Rental &&
            calculatedPriceResult &&
            calculatedPriceResult.componentPrices ? (
              calculatedPriceResult.componentPrices.map((componentPrice) => {
                return (
                  <div>
                    {safeRound(componentPrice.consumedUnits * units * 1000) / 1000} x $
                    {currencyFormat.format(componentPrice.unitPrice.price / 100)}{' '}
                    {selectedPriceType === OrderType.Rental &&
                      `Per
                      ${componentPrice.unitPrice.durationValue} ${componentPrice.unitPrice.durationUnit}${
                        componentPrice.unitPrice.durationValue > 1 ? 's' : ''
                      }`}
                  </div>
                );
              })
            ) : selectedPriceType === OrderType.Sale && calculatedSalePrice !== undefined ? (
              <div>
                {units} x ${currencyFormat.format(calculatedSalePrice / 100)}
              </div>
            ) : (
              'N/A'
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
